import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { BaseComponent } from '../../../../../../components/base.component';
import { Observable, Subscription } from 'rxjs';
import { VariableGroup } from '../../../../../models/VariableGroup';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DbSelectionChangeArg } from '../../../../../../shared/models/DbSelectionChangeArg';
import { Constants } from '../../../../../../shared/Constants';
import { switchMap } from 'rxjs/operators';
import { GetVariableValueByKey } from '../../../../../../shared/utils/VariableGroupUtils';
import { AzRelease } from '../../../../../models/AzRelease';
import { Release } from '../../../../../../shared/models/Release';
import { DbRestoreConfirmationComponent } from '../../../db-single-tenant-panel/db-restore-confirmation/db-restore-confirmation.component';
import { DialogAction } from '@progress/kendo-angular-dialog';

@Component({
  selector: 'app-db-single-tenant-restore-from-db-panel',
  templateUrl: './db-single-tenant-restore-from-db-panel.component.html',
  styleUrls: ['./db-single-tenant-restore-from-db-panel.component.scss']
})
export class DbSingleTenantRestoreFromDbPanelComponent extends BaseComponent implements OnDestroy {

  private sub: Subscription;

  @Output() completed = new EventEmitter();

  private _dcrc: boolean = false;
  private _dropTarDb: boolean = false;

  private _customerIdSource: string = '';
  private _sourceDb: string = '';
  private _sourceEnv: string = '';
  private _sourceServer: string = '';
  private _srcMISubscription: string = '';

  private _customerIdTarget: string = '';
  private _targetDb: string = '';
  private _targetEnv: string = '';
  private _targetServer: string = '';
  private _tgtMISubscription: string = '';

  private _selectedTargetVariableGroup: VariableGroup = null;

  public formGroup: UntypedFormGroup = this.fb.group({
    dcrc: [this._dcrc],
    dropTarDb: [this._dropTarDb],

    source: this.fb.group({
      CustomerId: [this._customerIdSource, Validators.required],
      Db: [this._sourceDb, Validators.required],
      Env: [this._sourceEnv, Validators.required],
      Server: [this._sourceServer, Validators.required],
      Subscription: [this._srcMISubscription, Validators.required]
    }),

    target: this.fb.group({
      CustomerId: [this._customerIdTarget, Validators.required],
      Db: [this._targetDb, Validators.required],
      Env: [this._targetEnv, Validators.required],
      Server: [this._targetServer, Validators.required],
      Subscription: [this._tgtMISubscription, Validators.required]
    })
  });

  public set customerIdTarget(value: string) {
    this.formGroup.get('target.CustomerId').setValue(value);
    this._customerIdTarget = value;
  }

  public set dcrc(value: boolean) {
    this.formGroup.controls.dcrc.setValue(value);
    this._dcrc = value;
  }

  public set dropTarDb(value: boolean) {
    this.formGroup.controls.dropTarDb.setValue(value);
    this._dropTarDb = value;
  }

  public set sourceCustomerId(value: string) {
    this.formGroup.get('source.CustomerId').setValue(value);
    this._sourceDb = value;
  }

  public get sourceCustomerId() {
    return this.formGroup.get('source.CustomerId').value;
  }

  public set sourceDb(value: string) {
    this.formGroup.get('source.Db').setValue(value);
  }

  public get sourceDb() {
    return this.formGroup.get('source.Db').value;
  }

  public set sourceEnv(value: string) {
    this.formGroup.get('source.Env').setValue(value);
    this._sourceEnv = value;
  }

  public get sourceEnv() {
    return this.formGroup.get('source.Env').value;
  }

  public set sourceServer(value: string) {
    this.formGroup.get('source.Server').setValue(value);
    this._sourceServer = value;
  }

  public get sourceServer() {
    return this.formGroup.get('source.Server').value;
  }

  public set srcMISubscription(value: string) {
    this.formGroup.get('source.Subscription').setValue(value);
    this._srcMISubscription = value;
  }

  public get srcMISubscription() {
    return this.formGroup.get('source.Subscription').value;
  }

  public set targetCustomerId(value: string) {
    this.formGroup.get('target.CustomerId').setValue(value);
    this._sourceDb = value;
  }

  public get targetCustomerId() {
    return this.formGroup.get('target.CustomerId').value;
  }

  public set targetDb(value: string) {
    this.formGroup.get('target.Db').setValue(value);
    this._targetDb = value;
  }

  public get targetDb() {
    return this.formGroup.get('target.Db').value;
  }

  public set targetEnv(value: string) {
    this.formGroup.get('target.Env').setValue(value);
    this._targetEnv = value;
  }

  public get targetEnv() {
    return this.formGroup.get('target.Env').value;
  }

  public set targetServer(value: string) {
    this.formGroup.get('target.Server').setValue(value);
    this._targetServer = value;
  }

  public get targetServer() {
    return this.formGroup.get('target.Server').value;
  }

  public set tgtMISubscription(value: string) {
    this.formGroup.get('target.Subscription').setValue(value);
    this._tgtMISubscription = value;
  }

  public get tgtMISubscription() {
    return this.formGroup.get('target.Subscription').value;
  }

  public set Source(val: DbSelectionChangeArg) {
    this.parseSource(val);
  }

  public set Target(val: DbSelectionChangeArg) {
    this.parseTarget(val);
  }

  constructor(private fb: UntypedFormBuilder) {
    super();
  }

  ngOnDestroy(): void {
    if(this.sub){
      this.sub.unsubscribe();
    }
  }

  onStartDbRestore() {
    const dialogRef = this.dialogService.open({
      content: DbRestoreConfirmationComponent,
    });

    const instance = dialogRef.content.instance;

    instance.Source = {
      'CustomerId': this.sourceCustomerId,
      'Db': this.sourceDb,
      'Env': this.sourceEnv,
      'Server': this.sourceServer,
      'Subscription': this.srcMISubscription,
    };

    instance.Target = {
      'CustomerId': this.targetCustomerId,
      'Db': this.targetDb,
      'Env': this.targetEnv,
      'Server': this.targetServer,
      'Subscription': this.tgtMISubscription,
    };

    this.sub = dialogRef.result.subscribe((r) => {
      const result = r as DialogAction;
      if (result.themeColor === Constants.THEME_PRIMARY) {
        this.startDbRestore();
      }
    });
  }

  private startDbRestore() {
    const variables = {
      'CustomerID': {
        'value': this.targetCustomerId,
      },
      'DCRC': {
        'value': this.dcrc ? '1' : '0',
      },
      'DropTarDB': {
        'value': this.dropTarDb ? '1' : '0',
      },
      'SourceDB': {
        'value': this.sourceDb,
      },
      'SourceServer': {
        'value': this.sourceServer,
      },
      'SourceSubscription': {
        'value': this.srcMISubscription,
      },
      'TargetDB': {
        'value': this.targetDb,
      },
      'TargetEnv': {
        'value': this.targetEnv,
      },
      'TargetServer': {
        'value': this.targetServer,
      },
      'TargetSubscription': {
        'value': this.tgtMISubscription,
      }
    };

    const releaseRequest = {
      'definitionId': +this.configService.Pipelines.DbRestoreFromExistingDbReleaseDefinitionId,
      'description': 'Started from DevOpsPortal',
      'artifacts': [],
      'isDraft': false,
      'reason': 'none',
      'manualEnvironments': null,
      'variables': variables
    };

    this.loaderService.start();
    this.sub = this.releaseService.createRelease(this.configService.MainProject, releaseRequest)
      .pipe(
        switchMap((re) => this.addReleaseToDb(re, variables))
      )
      .subscribe({
        next: (re) => {
          this.toastr.success(`DB restore pipeline was started successfully: ${re.name}!`, 'Success!')
            .onHidden.subscribe(() => {
            this.router.navigate([`${Constants.DB_RESTORE_LOGS}`])
              .then();
          });
          this.loaderService.stop();
          this.resetForm();
        },
        error: (error) => {
          console.error(error);
          this.loaderService.stop();
        },
        complete: () => {
          this.loaderService.stop();
          this.resetForm();
        }
      });
  }


  private resetForm() {
    this.dcrc = false;
    this.dropTarDb = false;

    this.customerIdTarget = null;
    this.sourceDb = null;
    this.sourceEnv = null;
    this.sourceServer = null;
    this.srcMISubscription = null;

    this.targetDb = null;
    this.targetEnv = null;
    this.targetServer = null;
    this.tgtMISubscription = null;

    this.completed.emit();
  }

  private parseSource(source: DbSelectionChangeArg) {
    const vg = source.VariableGroup as VariableGroup
    const data = vg?.variables;
    const env = source.Environment;

    if (!data) {
      return;
    }

    const customerId = GetVariableValueByKey(data, Constants.Customer_Id);
    const meshId = GetVariableValueByKey(data, Constants.Mesh_Id);
    const offering = GetVariableValueByKey(data, Constants.Offering);
    const timeZoneAbbreviated = GetVariableValueByKey(data, Constants.TimeZoneAbbreviated);
    const instance = GetVariableValueByKey(data, Constants.Instance);

    this.sourceCustomerId = `${customerId}`;
    this.sourceDb = `${customerId}-${env === 'prd' ? 'live' : env}`;
    this.sourceEnv = `${env}`;
    this.sourceServer = `${meshId}-${offering}-${timeZoneAbbreviated}-sqlmi-${instance}`;
    this.srcMISubscription = DbSingleTenantRestoreFromDbPanelComponent.getSubscriptionFromServerName(this._sourceServer);
  }

  private parseTarget(target: DbSelectionChangeArg) {
    const vg = target.VariableGroup as VariableGroup;
    this._selectedTargetVariableGroup = vg;
    const data = vg?.variables;
    const env = target.Environment;

    if (!data) {
      return;
    }

    this.customerIdTarget = GetVariableValueByKey(data, Constants.Customer_Id);

    const customerId = GetVariableValueByKey(data, Constants.Customer_Id);
    const meshId = GetVariableValueByKey(data, Constants.Mesh_Id);
    const offering = GetVariableValueByKey(data, Constants.Offering);
    const timeZoneAbbreviated = GetVariableValueByKey(data, Constants.TimeZoneAbbreviated);
    const instance = GetVariableValueByKey(data, Constants.Instance);

    this.targetCustomerId = `${customerId}`;
    this.targetDb = `${customerId}-${env === 'prd' ? 'live' : env}`;
    this.targetEnv = `${env}`;
    this.targetServer = `${meshId}-${offering}-${timeZoneAbbreviated}-sqlmi-${instance}`;
    this.tgtMISubscription = DbSingleTenantRestoreFromDbPanelComponent.getSubscriptionFromServerName(this._targetServer);
  }

  private static getSubscriptionFromServerName(server: string) {
    const firstChar = server[0];

    const subscriptions = {
      '1': 'Customer-Mesh-prd-01',
      '3': 'Customer-Mesh-npd-01',
      '9': 'Dev-Mesh-01'
    };

    return subscriptions[firstChar] || 'Sales-Mesh-01';
  }

  disableStartDbRefresh() {
    return this.formGroup.status === 'INVALID';
  }

  private addReleaseToDb(re: AzRelease, variables: any): Observable<Release> {
    const user = this.msalService.instance.getActiveAccount().name;
    const release: Release = {
      'id': re.id,
      'batchId': Constants.DB_RESTORE_FROM_DB,
      'name': `${user}`,
      'batch': undefined,
      'projectName': this.configService.MainProject,
      'customerId': null,
      'customerName': JSON.stringify(variables),
      'stage': null,
      'createdBy': user,
      'createdDate': new Date(),
      'modifiedBy': user,
      'modifiedDate': new Date(),
      'azBuild': undefined
    };
    return this.releaseDbService.createRelease(release);
  }
}
